/**
 * Upgrade Promotion Templates
 * Drive plan upgrades and expansions
 */

import { PromotionTemplate } from "./types";

export const UPGRADE_TEMPLATES: PromotionTemplate[] = [
  {
    id: "trial-to-paid",
    name: "Trial Conversion",
    category: "upgrade",
    description: "Convert trial users before their trial expires",
    strategy: "Trial users have experienced your product's value. A well-timed discount as trial ends can push them over the conversion edge. Best triggered 3 days before trial expiry.",
    icon: "ClockCircleOutlined",
    color: "#3B82F6",
    expectedOutcomes: [
      "15-30% trial conversion lift",
      "Reduced trial-to-churn rate",
      "Faster revenue realization",
    ],
    bestPractices: [
      "Trigger 3-5 days before trial ends",
      "Remind them of value they'll lose",
      "Create urgency with countdown",
      "Offer first-month discount",
    ],
    tags: ["upgrade", "trial", "conversion", "time-sensitive"],
    bestFor: ["Freemium products", "Products with trials"],
    difficulty: "beginner",
    setupTime: "10 min",
    defaults: {
      type: "popup",
      displayStyle: "bold",
      priority: 9,
      targetAudience: ["trial"],
      discount: {
        discountType: "percentage",
        discountValue: 30,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "gradient_blue",
        primaryColor: "#3B82F6",
        secondaryColor: "#6366F1",
        animation: "pulse",
      },
      displayConditions: {
        showOnPages: ["dashboard", "pricing"],
        maxImpressionsPerUser: 5,
        delaySeconds: 2,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Your Trial Ends Soon!",
          subheadline: "Don't lose access. Subscribe now and get 30% off your first month.",
          ctaText: "Subscribe & Save",
          ctaSecondaryText: "Compare Plans",
          badge: "TRIAL ENDING",
          terms: "*30% off first month for new subscribers.",
        },
      },
    },
  },
  {
    id: "free-to-pro",
    name: "Free to Pro Upgrade",
    category: "upgrade",
    description: "Encourage free users to upgrade to paid plans",
    strategy: "Free users who actively use your product are prime upgrade candidates. Show them what they're missing and offer a discount to reduce friction.",
    icon: "CrownOutlined",
    color: "#8B5CF6",
    expectedOutcomes: [
      "Increased free-to-paid conversion",
      "Revenue from free user base",
      "Reduced free tier costs",
    ],
    bestPractices: [
      "Target active free users",
      "Show features they can't access",
      "Highlight value of upgrade",
      "Offer limited-time discount",
    ],
    tags: ["upgrade", "free-to-paid", "conversion", "monetization"],
    bestFor: ["Freemium products", "Products with feature limits"],
    difficulty: "beginner",
    setupTime: "10 min",
    defaults: {
      type: "popup",
      displayStyle: "standard",
      priority: 7,
      targetAudience: ["free"],
      discount: {
        discountType: "percentage",
        discountValue: 40,
        currency: "USD",
        applicablePlans: ["pro"],
      },
      styling: {
        theme: "gradient_purple",
        primaryColor: "#8B5CF6",
        secondaryColor: "#EC4899",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["dashboard"],
        maxImpressionsPerUser: 5,
        delaySeconds: 5,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Unlock the Full Power",
          subheadline: "You're hitting your limits! Upgrade to Pro with 40% off.",
          ctaText: "Upgrade to Pro",
          ctaSecondaryText: "See Pro Features",
          badge: "40% OFF",
          terms: "*Limited time offer for free users.",
        },
      },
    },
  },
  {
    id: "pro-to-enterprise",
    name: "Pro to Enterprise",
    category: "upgrade",
    description: "Encourage Pro users to upgrade to Enterprise",
    strategy: "Power users on Pro plans often need enterprise features. Identify users hitting limits or requesting features available on Enterprise, then offer upgrade incentive.",
    icon: "RocketOutlined",
    color: "#10B981",
    expectedOutcomes: [
      "Higher ARPU",
      "Enterprise customer acquisition",
      "Reduced support burden",
    ],
    bestPractices: [
      "Target users hitting Pro limits",
      "Highlight enterprise-only features",
      "Offer personalized onboarding",
      "Provide ROI justification",
    ],
    tags: ["upgrade", "enterprise", "expansion", "b2b"],
    bestFor: ["B2B SaaS", "Products with enterprise tier"],
    difficulty: "intermediate",
    setupTime: "15 min",
    defaults: {
      type: "banner",
      displayStyle: "minimal",
      priority: 6,
      targetAudience: ["paid"],
      discount: {
        discountType: "percentage",
        discountValue: 25,
        currency: "USD",
        applicablePlans: ["enterprise"],
      },
      styling: {
        theme: "default",
        primaryColor: "#10B981",
        secondaryColor: "#3B82F6",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["dashboard", "billing"],
        maxImpressionsPerUser: 3,
        delaySeconds: 10,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Ready for Enterprise?",
          subheadline: "Get team features, priority support, and more. 25% off first year.",
          ctaText: "Explore Enterprise",
          ctaSecondaryText: "Talk to Sales",
          badge: "ENTERPRISE",
          terms: "*Schedule a demo to unlock offer.",
        },
      },
    },
  },
  {
    id: "annual-upgrade",
    name: "Annual Plan Upgrade",
    category: "upgrade",
    description: "Convert monthly subscribers to annual plans",
    strategy: "Annual plans improve cash flow and reduce churn. Offer significant savings to encourage monthly users to commit annually.",
    icon: "CalendarOutlined",
    color: "#F59E0B",
    expectedOutcomes: [
      "Improved cash flow",
      "Lower churn rate",
      "Higher LTV",
    ],
    bestPractices: [
      "Offer 2+ months free equivalent",
      "Show total annual savings",
      "Target after 3+ months of subscription",
      "Highlight commitment benefits",
    ],
    tags: ["upgrade", "annual", "retention", "commitment"],
    bestFor: ["Subscription products", "SaaS"],
    difficulty: "beginner",
    setupTime: "10 min",
    defaults: {
      type: "popup",
      displayStyle: "standard",
      priority: 5,
      targetAudience: ["paid"],
      discount: {
        discountType: "percentage",
        discountValue: 20,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "default",
        primaryColor: "#F59E0B",
        secondaryColor: "#10B981",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["billing"],
        maxImpressionsPerUser: 3,
        delaySeconds: 5,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Save with Annual",
          subheadline: "Switch to annual billing and save 20%. That's 2+ months free!",
          ctaText: "Switch to Annual",
          ctaSecondaryText: "Maybe Later",
          badge: "SAVE 20%",
          terms: "*Savings compared to monthly billing.",
        },
      },
    },
  },
];
